import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { Table, Button, Modal, Form, Card } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';

const OPTION_TYPES = ['유료옵션', '할증', '무료서비스'];

const ManageOptions = () => {
    const { user } = useContext(AuthContext);
    const [options, setOptions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentOption, setCurrentOption] = useState({});

    const canManageOptions = hasPermission(user?.role, 'settings:options');

    const fetchOptions = useCallback(async () => {
        if (!user) return;
        try {
            const response = await api.get('/options');
            setOptions(response.data.data);
        } catch (error) {
            console.error("Error fetching options:", error);
        }
    }, [user]);

    useEffect(() => {
        if (user) fetchOptions();
    }, [user, fetchOptions]);

    const groupedOptions = useMemo(() => {
        const groups = {};
        OPTION_TYPES.forEach(type => {
            groups[type] = options
                .filter(o => o.type === type)
                .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.optionName.localeCompare(b.optionName));
        });
        return groups;
    }, [options]);

    const handleShowModal = (option = null) => {
        setIsEditing(option !== null);
        setCurrentOption(option
            ? { ...option }
            : { optionName: '', type: OPTION_TYPES[0], is_app_visible: false }
        );
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleSave = async () => {
        if (!user) { alert('로그인이 필요합니다.'); return; }
        try {
            if (isEditing) {
                await api.put(`/options/${currentOption.id}`, currentOption);
            } else {
                await api.post('/options', currentOption);
            }
            fetchOptions();
            handleCloseModal();
        } catch (error) {
            console.error("Error saving option:", error);
        }
    };

    const handleToggleActive = async (id) => {
        if (!user) { alert('로그인이 필요합니다.'); return; }
        try {
            await api.put(`/options/${id}/toggle`);
            fetchOptions();
        } catch (error) {
            console.error("Error toggling option status:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const next = type === 'checkbox' ? checked : value;
        setCurrentOption(prev => ({ ...prev, [name]: next }));
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;
        const type = result.source.droppableId;
        const items = Array.from(groupedOptions[type]);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);

        // 로컬 상태 즉시 반영
        const newOptions = options.map(o => {
            if (o.type !== type) return o;
            const idx = items.findIndex(i => i.id === o.id);
            return idx >= 0 ? { ...o, displayOrder: idx + 1 } : o;
        });
        setOptions(newOptions);

        try {
            await api.put('/options/reorder', { order: items.map(o => ({ id: o.id })) });
        } catch (error) {
            console.error("Error saving order:", error);
            fetchOptions();
        }
    };

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>옵션 관리</h3>
                    <p>서비스 추가 옵션을 관리합니다</p>
                </div>
                <div className="page-header-actions">
                    {canManageOptions && (
                        <Button variant="primary" size="sm" onClick={() => handleShowModal()}>새 옵션 추가</Button>
                    )}
                </div>
            </div>
            <Card>
                <Card.Body>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        {OPTION_TYPES.map(type => (
                            <div key={type} className="mb-4">
                                <h5>{type}</h5>
                                <Table hover responsive>
                                    <thead>
                                        <tr>
                                            {canManageOptions && <th style={{ width: 40 }}></th>}
                                            <th>옵션명</th>
                                            <th>상태</th>
                                            <th>관리</th>
                                        </tr>
                                    </thead>
                                    <Droppable droppableId={type}>
                                        {(provided) => (
                                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                {groupedOptions[type] && groupedOptions[type].length > 0 ? (
                                                    groupedOptions[type].map((option, index) => (
                                                        <Draggable key={String(option.id)} draggableId={String(option.id)} index={index} isDragDisabled={!canManageOptions}>
                                                            {(provided, snapshot) => (
                                                                <tr ref={provided.innerRef} {...provided.draggableProps}
                                                                    style={{ ...provided.draggableProps.style, background: snapshot.isDragging ? '#f0f0f0' : undefined }}>
                                                                    {canManageOptions && <td {...provided.dragHandleProps} style={{ cursor: 'grab', textAlign: 'center' }}>☰</td>}
                                                                    <td>{option.optionName}</td>
                                                                    <td>{option.isActive ? <span className="text-success">활성</span> : <span className="text-danger">비활성</span>}</td>
                                                                    <td>
                                                                        {canManageOptions && (
                                                                            <>
                                                                                <Button variant="info" size="sm" onClick={() => handleShowModal(option)}>수정</Button>{' '}
                                                                                <Button variant={option.isActive ? 'warning' : 'success'} size="sm" onClick={() => handleToggleActive(option.id)}>
                                                                                    {option.isActive ? '비활성화' : '활성화'}
                                                                                </Button>
                                                                            </>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </Draggable>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan={canManageOptions ? 4 : 3} className="text-center">해당 타입의 옵션이 없습니다.</td></tr>
                                                )}
                                                {provided.placeholder}
                                            </tbody>
                                        )}
                                    </Droppable>
                                </Table>
                            </div>
                        ))}
                    </DragDropContext>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? '옵션 수정' : '새 옵션 추가'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="optionName">
                            <Form.Label>옵션명</Form.Label>
                            <Form.Control type="text" name="optionName" value={currentOption.optionName || ''} onChange={handleChange} disabled={!canManageOptions} />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="optionType">
                            <Form.Label>타입</Form.Label>
                            <Form.Select name="type" value={currentOption.type || OPTION_TYPES[0]} onChange={handleChange} disabled={!canManageOptions}>
                                {OPTION_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                            </Form.Select>
                        </Form.Group>
                        {/* 고객 앱 노출(is_app_visible) 토글은 숨겨둔다 — ManageCourses 와 같은 이유. */}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>닫기</Button>
                    <Button variant="primary" onClick={handleSave} disabled={!canManageOptions}>저장</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ManageOptions;
